import React, { useState, useContext } from 'react';
import { TableOptions } from 'types/Table';
import DataTable from '../../../partials/DataTable';
import { withRouter, RouteComponentProps } from 'react-router';
import { useQuery, useApolloClient, useSubscription } from '@apollo/react-hooks';
import { Card } from 'antd';
import { buildQuery, buildDeleteMutation, buildSubscriptionQuery, buildQueryById } from  '../../../utils/queryBuilder';
import toCamelCase from '../../../utils/toCamelCase';
import { AppContext } from 'utils/AppContext';
import { isAdminRole } from 'utils/roles';

interface ICommentsProps {
  networkPolicy?: 'network-only' | 'cache-first' | 'cache-only' | 'no-cache' | 'standby' | 'cache-and-network' | undefined,
  rowSelection?: any,
  rowSelectionActions?: any,
  onRowClick?: (row: any, event: any) => void,
  actions?: any[],
  commentsMeta: any,
  urlPrefix?: string,
  conditions?: any,
  context?: any,
  wrap?: boolean,
  pagination?: boolean,
  createTab: (path: string, title: string) => void;
  filterComponent?: JSX.Element,
  customName?: {title: string, singular: string},
  tableOptions?: TableOptions,
}

const Comments = (props: ICommentsProps & RouteComponentProps) => {
  const { getRole } = useContext(AppContext);

  const {
    commentsMeta,
    history: { push },
    location: { pathname, search },
    createTab, urlPrefix,
    conditions, onRowClick,
    networkPolicy,
    rowSelection, rowSelectionActions,
    wrap,
    context,
    filterComponent,
    customName,
    tableOptions,
  } = props;

  const searchQuery = new URLSearchParams(search);

  const urlQueryFilter = new URLSearchParams(search);
  const filterQueryKey = `${toCamelCase(commentsMeta.title).toLowerCase()}_conditions`;

  const filterQuerySearchKey = `${filterQueryKey}_query`;

  const filterQuery = {
    ...(urlQueryFilter.get(filterQueryKey) ? JSON.parse(urlQueryFilter.get(filterQueryKey) as string) : {}),
    ...(urlQueryFilter.get(filterQuerySearchKey) ? {querySearch: urlQueryFilter.get(filterQuerySearchKey)} : {}),
  }

  const mergedFilter = {...conditions, ...filterQuery};
  const [lastEvent, setLastEvent] = useState({} as any);
  const [fetchNetwork, setFetchNetwork] = useState(true);
  const pagination = props.pagination === false
    ? undefined
    : { pageSize: String(searchQuery.get('pageSize') || 40), page: String(searchQuery.get('page') || 1) }

  const orderBy = commentsMeta.table.defaultSort && {
        column: commentsMeta.table.defaultSort.column,
        direction: commentsMeta.table.defaultSort.direction || 'DESC'
    };

  const query = buildQuery(commentsMeta, mergedFilter, pagination, orderBy, context);
  const { loading, data } = useQuery(query, { fetchPolicy: networkPolicy || (fetchNetwork ? 'network-only' : 'cache-first') });
  const [working, setWorking] = useState(false);
  const client = useApolloClient();

  if (fetchNetwork) {
    setFetchNetwork(false);
  }


  const deleteComments = (id: string) => {
    return client.mutate({
      mutation: buildDeleteMutation(commentsMeta),
      variables: { id },
    });
  };

  const { data: subscriptionData } = useSubscription(buildSubscriptionQuery(commentsMeta));
  if (subscriptionData && subscriptionData[toCamelCase(commentsMeta.singular).toLowerCase()]) {
    const { mutation, node  } = subscriptionData[toCamelCase(commentsMeta.singular).toLowerCase()];
    let data = client.readQuery({ query }) as any;
    const records = data[toCamelCase(commentsMeta.title).toLowerCase()];

    /**
     * Execute update only when there is chagne in state
     * For example, 2 updates on update won't be affecting cache
     * But same id with different mutation will be updated (create & delete right after)
     */
    if(((lastEvent && lastEvent.id) !== (node && node.id)) || (mutation !== (lastEvent && lastEvent.mutation)) || mutation === 'update') {
      switch(mutation) {
        case 'delete':
          setWorking(true);
          client.writeQuery({
            query: query,
            data: { comments: records.filter(({ id }: any) => id !== node.id) },
          });
          setWorking(false);
          setLastEvent({ id: node.id, mutation });
          break;
        case 'create':
          client.query({
            query: buildQueryById(commentsMeta),
            variables: { id: node.id },
            fetchPolicy: 'network-only',
          }).then(({ data }: any) => {
            const existingRecord = records.find((r: any) => r.id === node.id);
            if(!existingRecord) {
              const record = data[toCamelCase(commentsMeta.singular).toLowerCase()]
              let pushRecordToCollection = true;

              if (context) {
                pushRecordToCollection = Object.keys(context).reduce((acc: boolean, contextKey: string) => {
                  return (record && record[contextKey] === context[contextKey]) || false;
                }, true);
              }

              if (pushRecordToCollection) {
                records.push(record);
              }

              client.writeQuery({
                query,
                data: { comments: records },
              });
              setWorking(false);
              setLastEvent({ id: node.id, mutation });
            }
          });
          break;
        case 'update':
            client.query({
              query: buildQueryById(commentsMeta),
              variables: { id: node.id },
              fetchPolicy: 'network-only',
            }).then(({ data }: any) => {
              const existingRecordIndex = records.findIndex((r: any) => {
                return r.id === node.id && node.updatedAt !== r.updatedAt
              });

              if(!existingRecordIndex) {
                records[existingRecordIndex] = data[toCamelCase(commentsMeta.singular).toLowerCase()]
                client.writeQuery({
                  query,
                  data: { comments: records },
                });
                setWorking(false);
                setLastEvent({ id: node.id, mutation });
              }
            });
          break;
      }
    }
  }

  if (loading || working) {
    return <Card title="Loading..." loading={true} />
  }

  const nextSearchQuery = new URLSearchParams();
  const returnUrl = `${pathname}${search}`;
  nextSearchQuery.set('returnUrl', returnUrl);
  if (mergedFilter) {
    nextSearchQuery.set(filterQueryKey, JSON.stringify(mergedFilter));
  }

  let actions = props.actions || [
    { icon: 'copy', title: 'Copy', action: (row: any) => {
      // build url
      if (context) {
        nextSearchQuery.set(filterQueryKey, JSON.stringify(context));
      }

      nextSearchQuery.set('copy', 'true');
      const url = urlPrefix
        ? `${urlPrefix}${row.id}?${nextSearchQuery.toString()}`
        : `${pathname}/${row.id}?${nextSearchQuery.toString()}`;

      push(url);
    }},
    { icon: 'delete', title: 'Delete', action: (row: any) => {
      deleteComments(row.id);
     } },
  ];

  actions = isAdminRole(getRole()) ? actions : actions.filter((action) => action.title !== 'Delete');

  return <>
    <DataTable
      tableOptions={tableOptions}
      wrap={wrap}
      context={context}
      onRowClick={(row: any, event: any) => {
        if(onRowClick) {
          event.persist();
          onRowClick(row, event);
          return;
        }

        // build url
        if (context) {
          nextSearchQuery.set(filterQueryKey, JSON.stringify(context));
        }

        const url =
          urlPrefix
            ? `${urlPrefix}${row.id}?${nextSearchQuery.toString()}`
            : `${pathname}/${row.id}?${nextSearchQuery.toString()}`;

        // when shift or command is hold, open in new window
        if (event && (event.shiftKey || event.metaKey)) {
          window.open(url);
          return;
        }

        // in other case open in current window
        createTab(url, row.name);
        push(url);
      }}
      actions={actions}
      rowSelectionActions={rowSelectionActions}
      rowKey={'id'}
      rowSelection={rowSelection}
      meta={commentsMeta}
      data={data.comments}
      pagination={props.pagination !== false}
      filterComponent={filterComponent}
      customName={customName}
    />
  </>
};

export default withRouter(Comments);
